<template>
  <template v-if="agentSetup.isEditMode === true">
    <div v-show="agentSetup.currentPage === 6" class="bg-base-100 border border-base-300 rounded-box p-6 shadow-sm" style="min-height: 40vh;">
      <div class="flex justify-between" id="tabTitle">
        <h1 class="text-2xl" style="margin-bottom: 0%;" :style="agentSetup.isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ agentSetup.t('aiChatAgentSetup.fineTuneQA.title', 'Fine Tune Q&A') }}</h1>
      </div>
      <FineTuneAgent
        :selected-method="selectedFineTuneMethod"
        :show-content="false"
        @update:selected-method="selectedFineTuneMethod = $event"
      />
      <div v-if="selectedFineTuneMethod === 'answers'" role="tablist" class="tabs tabs-lifted mt-4">
        <input type="radio" name="fine_tune_tabs_light" role="tab" class="tab" :aria-label="agentSetup.t('aiChatAgentSetup.fineTuneQA.fineTuneTab', 'Fine Tune')" :checked="agentSetup.activeFineTuneTab === 'fineTune'" @click="agentSetup.activeFineTuneTab = 'fineTune'" />
        <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
          <div class="mt-4">
            <FineTuneAgent
              :agentID="agentSetup.route.query.edit"
              :selected-method="selectedFineTuneMethod"
              :show-method-selector="false"
              @save-fine-tune="agentSetup.handleFineTuneSave"
              @learn-question-answers="learnQuestionAnswers"
              @approve-generated-rules="agentSetup.approveGeneratedRulesFromAdvanced"
            />
          </div>
        </div>
        <input type="radio" name="fine_tune_tabs_light" role="tab" class="tab" :aria-label="agentSetup.t('aiChatAgentSetup.fineTuneQA.fineTunedAnswersTab', 'Fine Tuned Answers')" :checked="agentSetup.activeFineTuneTab === 'fineTunedAnswers'" @click="agentSetup.activeFineTuneTab = 'fineTunedAnswers'" />
        <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
          <h2 class="text-xl mb-4">{{ agentSetup.t('aiChatAgentSetup.fineTuneQA.existingAnswersTitle', 'Existing Fine-Tuned Answers (Assigned to Folders)') }}</h2>
          <p class="text-sm text-gray-500 mb-2">
            {{ agentSetup.t('aiChatAgentSetup.fineTuneQA.existingAnswersDescription', 'Fine-tuned answers are treated as files and can be organized into folders. They are primarily managed here but their folder assignment happens in the Knowledge Base tab.') }}
          </p>
          <div class="overflow-x-auto">
            <table class="table w-full">
              <thead>
                <tr>
                  <th>{{ agentSetup.t('aiChatAgentSetup.fineTuneQA.tableHeaders.row', 'Row #') }}</th>
                  <th>{{ agentSetup.t('aiChatAgentSetup.fineTuneQA.tableHeaders.originalQuestion', 'Original Question (Preview)') }}</th>
                  <th>{{ agentSetup.t('aiChatAgentSetup.fineTuneQA.tableHeaders.fineTunedAnswer', 'Fine-Tuned Answer (Preview)') }}</th>
                  <th>{{ agentSetup.t('aiChatAgentSetup.fineTuneQA.tableHeaders.folder', 'Folder') }}</th>
                  <th>{{ agentSetup.t('aiChatAgentSetup.fineTuneQA.tableHeaders.actions', 'Actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(answerFile, index) in agentSetup.fineTunedAnswers" :key="'finedtuned-' + index">
                  <td>{{ index + 1 }}</td>
                  <td>{{ answerFile.originalQuery }}</td>
                  <td>
                    <div>{{ answerFile.fineTunedAnswer }}</div>
                    <div v-if="answerFile.forceExactAnswer === true" class="text-xs text-primary mt-1">{{ agentSetup.t('aiChatAgentSetup.knowledgeBase.forceExactAnswer', 'Force Exact Answer') }}</div>
                  </td>
                  <td>
                    {{ agentSetup.knowledgeBaseFileAssignments[answerFile.fileName] ? (agentSetup.knowledgeBaseFolders.find(f => f.id === agentSetup.knowledgeBaseFileAssignments[answerFile.fileName])?.name || agentSetup.t('aiChatAgentSetup.fineTuneQA.unknownFolder', 'Unknown Folder')) : agentSetup.t('aiChatAgentSetup.fineTuneQA.rootFolder', 'Knowledge Base Root') }}
                  </td>
                  <td>
                    <button @click="agentSetup.removeFineTunedAnswer(index)" class="btn btn-error btn-sm">{{ agentSetup.t('aiChatAgentSetup.fineTuneQA.actions.remove', 'Remove') }}</button>
                  </td>
                </tr>
                <tr v-if="agentSetup.fineTunedAnswers.length === 0">
                  <td colspan="5" class="text-center">{{ agentSetup.t('aiChatAgentSetup.fineTuneQA.noAnswers', 'No fine-tuned answers yet.') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-else class="mt-4">
        <FineTuneAgent
          :agentID="agentSetup.route.query.edit"
          :selected-method="selectedFineTuneMethod"
          :show-method-selector="false"
          @save-fine-tune="agentSetup.handleFineTuneSave"
          @learn-question-answers="learnQuestionAnswers"
          @approve-generated-rules="agentSetup.approveGeneratedRulesFromAdvanced"
        />
      </div>
      <div class="flex justify-between mt-4" style="position: relative;">
        <button @click="agentSetup.goNextStep(6, -1)" class="btn">
          <span v-if="agentSetup.isNavigating === 5" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
        </button>
        <div class="flex items-center space-x-2" :class="agentSetup.isRtl === true ? 'space-x-reverse' : ''">
          <button v-if="agentSetup.isEditMode === true && agentSetup.uploadedFiles.length === 0" @click="agentSetup.saveAgentWithoutRedirect" class="btn btn-neutral">
            <span v-if="agentSetup.isSaving === true && agentSetup.isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
            {{ agentSetup.t('aiChatAgentSetup.buttons.save', 'Save') }}
          </button>
          <button v-if="agentSetup.isEditMode === true" @click="agentSetup.createAgent(true)" class="btn">
            <span v-if="agentSetup.isSaving === true && agentSetup.isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
            {{ agentSetup.t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
          </button>
          <button @click="agentSetup.goNextStep(6, 1)" class="btn">
            <span v-if="agentSetup.isNavigating === 7" class="loading loading-spinner loading-sm mr-2"></span>
            {{ agentSetup.t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
          </button>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup>
import { ref } from 'vue';
import FineTuneAgent from '@/components/FineTuneAgent.vue';
import { useAgentSetupContext } from '@/components/AgentSetupContext.js';

const agentSetup = useAgentSetupContext();
const selectedFineTuneMethod = ref('answers');

function learnQuestionAnswers(entries) {
  const source = Array.isArray(entries) === true ? entries : [];
  for (let index = 0; index < source.length; index = index + 1) {
    agentSetup.saveQuestionAnswerEntry(source[index]);
  }
}
</script>
